import { useEffect, useEffectEvent } from "react";

// The results-rail shortcuts share one document listener so they work after
// focusing the conversation, the audio strip, or the rail itself. Native
// editors and transient layers (Drawer, Menu, listbox) own their keys.
//
//   A agree · D disagree · F flag · Enter save-and-next ·
//   [ / ] previous / next call in the feed · Esc back
export function useLabelShortcuts({
  enabled,
  feed,
  onAgree,
  onBack,
  onDisagree,
  onFlag,
  onNext,
  onPrevious,
  onSaveNext,
}: {
  enabled: boolean;
  feed: boolean;
  onAgree?: () => void;
  onBack: () => void;
  onDisagree?: () => void;
  onFlag?: () => void;
  onNext?: () => void;
  onPrevious?: () => void;
  onSaveNext?: () => void;
}) {
  const handle = useEffectEvent((event: KeyboardEvent) => {
    if (event.defaultPrevented || event.isComposing || event.repeat) {
      return;
    }
    const target = event.target;
    if (target instanceof Element) {
      if (target.closest('[role="dialog"], [role="menu"], [role="listbox"]')) {
        return;
      }
      const editable = target.closest(
        'input, textarea, select, [contenteditable], [role="combobox"], ' +
          '[role="textbox"], [role="spinbutton"]',
      );
      if (editable && editable.getAttribute("contenteditable") !== "false") {
        // Suppress implicit form submission as well as the shortcut itself.
        if (event.key === "Enter" && (event.metaKey || event.ctrlKey)) {
          event.preventDefault();
        }
        return;
      }
    }
    if (event.key === "Escape" && !event.altKey && !event.shiftKey) {
      event.preventDefault();
      onBack();
      return;
    }
    if (!enabled) {
      return;
    }
    if (event.key === "Enter" && !event.altKey && !event.shiftKey) {
      // Plain Enter and the legacy Cmd/Ctrl+Enter both save and advance.
      event.preventDefault();
      onSaveNext?.();
      return;
    }
    if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) {
      return;
    }
    switch (event.key) {
      case "[":
        if (feed) {
          event.preventDefault();
          onPrevious?.();
        }
        break;
      case "]":
        if (feed) {
          event.preventDefault();
          onNext?.();
        }
        break;
      case "a":
      case "A":
        event.preventDefault();
        onAgree?.();
        break;
      case "d":
      case "D":
        event.preventDefault();
        onDisagree?.();
        break;
      case "f":
      case "F":
        event.preventDefault();
        onFlag?.();
        break;
      default:
        break;
    }
  });
  useEffect(() => {
    const listener = (event: KeyboardEvent) => {
      handle(event);
    };
    document.addEventListener("keydown", listener);
    return () => {
      document.removeEventListener("keydown", listener);
    };
  }, []);
}
